/**
 * The crash buffer: every part of a recording is written to IndexedDB
 * before it is uploaded and removed once storage has it, so a tab that
 * dies at minute thirty leaves a take on the disk that the next page load
 * can finish sending. Nothing is ever held only in memory.
 *
 * Two stores: `files` (one row per file being uploaded — which recording,
 * which room, how far it got) and `parts` (the blobs, keyed by file and
 * part number). Everything here is best effort: a browser that refuses
 * IndexedDB (private mode, a full disk) makes every call resolve to
 * nothing, and the recording still uploads — it just has no safety net.
 */

export interface BufferedFile {
  fileId: string
  /** The recording's id — or, for a capture (Phase 9), the video's, which is also the fileId. */
  recordingId: string
  /** Empty for a capture, which has no room. */
  roomSlug: string
  kind: 'composite' | 'per_track' | 'capture'
  mimeType: string
  label: string
  startedAt: number
  /** The recorder measured this much before it stopped, when it knows. */
  durationMs: number | null
  /** The recording is over and every part is written; only uploading remains. */
  finished: boolean
}

export interface BufferedPart {
  fileId: string
  partNumber: number
  blob: Blob
}

const DB_NAME = 'ls-recordings'
const DB_VERSION = 1

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null)
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains('files'))
          db.createObjectStore('files', { keyPath: 'fileId' })
        if (!db.objectStoreNames.contains('parts')) {
          const parts = db.createObjectStore('parts', { keyPath: ['fileId', 'partNumber'] })
          parts.createIndex('byFile', 'fileId')
        }
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function withDb<T>(fn: (db: IDBDatabase) => Promise<T>, fallback: T): Promise<T> {
  const db = await open()
  if (!db) return fallback
  try {
    return await fn(db)
  } catch {
    return fallback
  } finally {
    db.close()
  }
}

export const crashBuffer = {
  async rememberFile(file: BufferedFile): Promise<void> {
    await withDb(async (db) => {
      const tx = db.transaction('files', 'readwrite')
      tx.objectStore('files').put(file)
      await done(tx)
    }, undefined)
  },

  async updateFile(fileId: string, change: Partial<BufferedFile>): Promise<void> {
    await withDb(async (db) => {
      const tx = db.transaction('files', 'readwrite')
      const store = tx.objectStore('files')
      const current = (await request(store.get(fileId))) as BufferedFile | undefined
      if (current) store.put({ ...current, ...change })
      await done(tx)
    }, undefined)
  },

  async putPart(part: BufferedPart): Promise<void> {
    await withDb(async (db) => {
      const tx = db.transaction('parts', 'readwrite')
      tx.objectStore('parts').put(part)
      await done(tx)
    }, undefined)
  },

  async deletePart(fileId: string, partNumber: number): Promise<void> {
    await withDb(async (db) => {
      const tx = db.transaction('parts', 'readwrite')
      tx.objectStore('parts').delete([fileId, partNumber])
      await done(tx)
    }, undefined)
  },

  /** Every file that still has something to upload, oldest first. */
  async unfinishedFiles(): Promise<BufferedFile[]> {
    return withDb(async (db) => {
      const tx = db.transaction('files', 'readonly')
      const all = (await request(tx.objectStore('files').getAll())) as BufferedFile[]
      return all.sort((a, b) => a.startedAt - b.startedAt)
    }, [])
  },

  /** The parts of a file still on the disk, in order. */
  async partsOf(fileId: string): Promise<BufferedPart[]> {
    return withDb(async (db) => {
      const tx = db.transaction('parts', 'readonly')
      const all = (await request(
        tx.objectStore('parts').index('byFile').getAll(fileId),
      )) as BufferedPart[]
      return all.sort((a, b) => a.partNumber - b.partNumber)
    }, [])
  },

  /** The file is in storage (or given up): nothing of it stays here. */
  async forgetFile(fileId: string): Promise<void> {
    await withDb(async (db) => {
      const tx = db.transaction(['files', 'parts'], 'readwrite')
      tx.objectStore('files').delete(fileId)
      const parts = tx.objectStore('parts').index('byFile')
      const keys = (await request(parts.getAllKeys(fileId))) as IDBValidKey[]
      for (const key of keys) tx.objectStore('parts').delete(key)
      await done(tx)
    }, undefined)
  },
}
